import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import {
  EndOrderDetailDigitalPayment,
  EndOrderDetailPaymentStatus,
  PaymentMethod,
} from '@pedix-workspace/utils';
import { EnvironmentService } from '@pedix-workspace/angular-utils';

import { TranslocoDirective } from '@jsverse/transloco';
import { CurrencyFormatPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { DatePipe } from '@angular/common';
import {
  getOrderPaymentDisplayStatus,
  getOrderSurplusAmount,
  getPaymentUrl,
  getSuggestedRefundAmount,
} from '@pedix-workspace/orders';
import { PaymentStatusTagComponent } from '../../payment-status-tag/payment-status-tag.component';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { IconInfoComponent } from '@pedix-workspace/angular-ui-icons';
import { TooltipComponent } from '@pedix-workspace/angular-ui-tooltip';
import { TagComponent } from '@pedix-workspace/angular-ui-alert';

@Component({
  selector: 'pxw-order-details-payment-method',
  templateUrl: './order-details-payment-method.component.html',
  styleUrls: ['./order-details-payment-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    CurrencyFormatPipe,
    DatePipe,
    PaymentStatusTagComponent,
    ButtonComponent,
    IconInfoComponent,
    TooltipComponent,
    TagComponent,
  ],
})
export class OrderDetailsPaymentMethodComponent {
  private readonly environmentService = inject(EnvironmentService);

  readonly orderId = input.required<string>();
  readonly paymentMethod = input.required<PaymentMethod | null>();
  readonly collectedMethodsLabel = input('');
  readonly digitalPayment = input.required<EndOrderDetailDigitalPayment | null>();
  readonly howMuch = input.required<number | null>();
  readonly finalAmount = input<number>();
  readonly paidAmount = input(0);
  readonly tipAmount = input(0);
  readonly canRefund = input(false);
  readonly refundingPayment = input(false);
  readonly canMarkPaid = input(false);
  readonly canRegisterRefund = input(false);
  readonly canViewPayments = input(false);
  readonly collectedByMarketplaceLabel = input<string | null>(null);
  readonly showUnmatchedPayment = input(false);

  readonly refundPayment = output();
  readonly markPaid = output();
  readonly registerRefund = output();
  readonly viewPayments = output();

  readonly surplusAmount = computed(() => {
    return getOrderSurplusAmount(this.paidAmount(), this.finalAmount());
  });

  readonly refundableDisplayAmount = computed(() => {
    return getSuggestedRefundAmount(this.paidAmount(), this.finalAmount());
  });

  readonly paymentDisplayStatus = computed((): EndOrderDetailPaymentStatus | null => {
    return getOrderPaymentDisplayStatus({
      finalAmount: this.finalAmount(),
      paidAmount: this.paidAmount(),
      digitalPayment: this.digitalPayment(),
    });
  });

  readonly unmatchedPayment = computed(() => {
    return this.showUnmatchedPayment() ? (this.digitalPayment()?.unmatchedPayment ?? null) : null;
  });

  readonly paymentUrl = computed(() => {
    const payment = this.digitalPayment();

    if (!payment || payment.type !== 'link') {
      return null;
    }

    return getPaymentUrl(this.environmentService.siteUrl, this.orderId());
  });
}
